/**
 * Deployment environment guard (browser).
 *
 * Mirror of `server/api-util/environment.js`. Every Sentry event and span is
 * tagged with this value; Sentry defaults it to `production` when unset, so a
 * missing or misspelled `REACT_APP_SENTRY_ENVIRONMENT` is a hard failure instead
 * of a silent mislabel. This is a dedicated var, not `REACT_APP_ENV` (which is
 * `production` on every deploy). Keep the allowed set in sync with server + edge.
 */

/** The only deployment environments this marketplace runs in. */
export const ALLOWED_ENVIRONMENTS = ['development', 'test', 'production'];

/**
 * Assert that `value` is a recognised deployment environment.
 *
 * @param {string|undefined} value the raw `REACT_APP_SENTRY_ENVIRONMENT`
 * @returns {string} the same value, once validated
 * @throws {Error} when the value is missing or not in {@link ALLOWED_ENVIRONMENTS}
 */
export const assertValidEnvironment = value => {
  if (!ALLOWED_ENVIRONMENTS.includes(value)) {
    throw new Error(
      `REACT_APP_SENTRY_ENVIRONMENT must be one of: ${ALLOWED_ENVIRONMENTS.join(', ')} (got ${
        value == null ? '(unset)' : `"${value}"`
      }).`
    );
  }
  return value;
};

/**
 * Assert that `value` is a Sentry sample rate: a number in [0, 1]. There is no
 * fallback. Whenever Sentry is enabled the rate must be set explicitly, so a
 * missing or out-of-range value is a hard failure, not a silent guess.
 *
 * @param {string} name the env var name, used in the error message
 * @param {string|undefined} value the raw env value
 * @returns {number} the parsed rate, once validated
 * @throws {Error} when the value is missing or not in [0, 1]
 */
export const assertValidSampleRate = (name, value) => {
  const rate = Number.parseFloat(value);
  if (!Number.isFinite(rate) || rate < 0 || rate > 1) {
    throw new Error(
      `${name} must be a number between 0 and 1 (got ${value == null ? '(unset)' : `"${value}"`}).`
    );
  }
  return rate;
};
